import { DateTime } from 'luxon';

const REL_BIN_AGGREGATE_CHILD = 'https://api.binsentry.com/rel/bin-aggregate-child';

const READING_POLICIES = {
  default: {
    staleAfterMin: Infinity,
    oldAfterMin: Infinity,
  },
  ingredient: {
    staleAfterMin: 66,
    oldAfterMin: 180,
  },
  feed: {
    staleAfterMin: 270,
    oldAfterMin: 1440,
  },
};

export function getReadingPolicy(role) {
  return READING_POLICIES[role] || READING_POLICIES.default;
}

function getAggregateChildBins(binEntity) {
  if (!binEntity) {
    return [];
  }

  const byRel = binEntity.getSubEntitiesByRel?.(REL_BIN_AGGREGATE_CHILD);

  if (byRel?.length) {
    return byRel;
  }

  return binEntity.getSubEntities?.(REL_BIN_AGGREGATE_CHILD) || [];
}

export function resolveBinRole(binEntity) {
  const directRole = binEntity?.properties?.role;
  const type = binEntity?.properties?.type;

  if (type !== 'aggregate') {
    return directRole;
  }

  if (directRole) {
    return directRole;
  }

  return getAggregateChildBins(binEntity)
    .find(childBin => childBin?.properties?.role)
    ?.properties?.role;
}

export function getReadingStatusBooleans(status) {
  return {
    isFresh: status === 'fresh',
    isStale: status === 'stale',
    isOld: status === 'old',
    isUnknown: status === 'unknown',
    isMissing: status === 'missing',
  };
}

export function resolveReadingStatusFromTimestamp({
  recordedAtIso,
  staleAfterMin = getReadingPolicy().staleAfterMin,
  oldAfterMin = getReadingPolicy().oldAfterMin,
  hasValidValue = true,
  now = DateTime.now(),
} = {}) {
  if (!hasValidValue) {
    return {
      status: 'unknown',
      minutesSinceReading: null,
    };
  }

  const recordedAt = recordedAtIso ? DateTime.fromISO(recordedAtIso) : null;
  const minutesSinceReading = recordedAt?.isValid
    ? Math.max(0, -recordedAt.diff(now, 'minutes').minutes)
    : null;

  if (minutesSinceReading == null) {
    return {
      status: 'unknown',
      minutesSinceReading: null,
    };
  }

  const status = minutesSinceReading >= oldAfterMin
    ? 'old'
    : minutesSinceReading >= staleAfterMin
      ? 'stale'
      : 'fresh';

  return {
    status,
    minutesSinceReading,
  };
}

export function resolveCurrentBinReading({
  binEntity,
  binRole,
  latestLevelEntity,
  latestValidLevelEntity,
  now = DateTime.now(),
} = {}) {
  const effectiveBinRole = binRole ?? resolveBinRole(binEntity);
  const { staleAfterMin, oldAfterMin } = getReadingPolicy(effectiveBinRole);
  const latestLabel = latestLevelEntity?.properties?.label;
  const isOutlier = latestLabel === 'OUTLIER';

  const effectiveEntity = isOutlier ? latestValidLevelEntity : latestLevelEntity;

  if (!effectiveEntity) {
    return {
      levelEntity: null,
      isOutlier,
      status: 'missing',
      quantityVolume: null,
      recordedAtIso: null,
      minutesSinceReading: null,
      source: 'none',
    };
  }

  const { publishedAt, volume } = effectiveEntity.properties || {};
  const recordedAtIso = publishedAt || null;
  const { status, minutesSinceReading } = resolveReadingStatusFromTimestamp({
    recordedAtIso,
    staleAfterMin,
    oldAfterMin,
    now,
  });

  return {
    levelEntity: effectiveEntity,
    isOutlier,
    status,
    quantityVolume: volume ?? null,
    recordedAtIso,
    minutesSinceReading,
    source: isOutlier ? 'latest-valid' : 'latest',
  };
}
